import React, { useState, useEffect, createContext, useContext } from 'react'
import { Modal, SectionPanel, Spinner } from './ui'

const DetailContext = createContext(null)

export const useDetailContext = () => {
  const context = useContext(DetailContext)
  if (!context) {
    throw new Error(`useDetailContext doit être utilisé à l'intérieur d'un Detailtemplate`)
  }
  return context
}

/**
 * Template réutilisable pour les sections de détail avec fonctionnalité d'édition.
 * Ce composant permet d'afficher des informations structurées avec un titre,
 * un contenu personnalisable, et un bouton d'édition qui ouvre un modal pour l'édition.
 * 
 * @component
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.title - Le titre de la section à afficher
 * @param {string} [props.sectionId] - Identifiant unique de la section, utilisé pour les animations ciblées.
 *                                    Si fourni avec updatedSections, permet des animations spécifiques à cette section.
 * @param {Object} [props.bodyProps={}] - Propriétés additionnelles à passer au corps de la section (SectionPanel.Body).
 *                                      Exemple: { columns: 3 } pour définir le nombre de colonnes.
 * @param {React.ReactNode} props.children - Le contenu à afficher dans la section
 * @param {boolean} [props.wasUpdated=false] - Indique si l'objet a été mis à jour récemment (approche globale).
 *                                           Utilisé uniquement si sectionId n'est pas fourni.
 * @param {Object} [props.updatedSections={}] - Objet contenant les sections mises à jour sous forme { sectionId: true }.
 *                                            Utilisé avec sectionId pour des animations ciblées.
 * @param {boolean} [props.loading=false] - Indique si les données sont en cours de chargement
 * @param {boolean} [props.isPendingAction=false] - Indique si une action est en cours (désactive les boutons d'édition)
 * @param {React.ReactNode} [props.editButton=null] - Le bouton d'édition à afficher.
 *                                                  Recevra automatiquement onEditClick comme prop.
 * @param {React.ReactNode} [props.modalContent=null] - Le contenu à afficher dans le modal (généralement un formulaire).
 *                                                    Recevra automatiquement onClose et onSuccess comme props.
 * @param {Object} [props.modalProps={}] - Props additionnelles à passer au composant Modal
 * @param {Function} [props.onUpdateSuccess=null] - Fonction appelée lors d'une mise à jour réussie.
 *                                                Reçoit sectionId comme argument.
 * 
 * @example
 * // Exemple d'utilisation avec une approche par section
 * const MaSection = () => {
 *   const { bien, updatedSections, markSectionAsUpdated, isPendingAction } = useBienContext()
 *   const SECTION_ID = 'ma_section'
 *   
 *   return (
 *     <DetailTemplate
 *       title="Ma Section"
 *       sectionId={SECTION_ID}
 *       updatedSections={updatedSections}
 *       isPendingAction={isPendingAction}
 *       editButton={<EditButtons bienId={bien.id} />}
 *       modalContent={<Form />}
 *       onUpdateSuccess={markSectionAsUpdated}
 *     >
 *       <div>Contenu de ma section...</div>
 *     </DetailTemplate>
 *   )
 * }
 * 
 * @returns {JSX.Element}
 */

const DetailTemplate = ({
  title,
  sectionId,
  bodyProps = {},
  children,
  wasUpdated = false,
  updatedSections = {},
  loading = false,
  isPendingAction = false,
  editButton = null,
  modalContent = null,
  modalProps = {},
  onUpdateSuccess = null,
  onCloseModalCallback = null,
  showBackdrop = false,
  showCloseButton = false
}) => {
  const [modalVisible, setModalVisible] = useState(false)
  const [animateUpdate, setAnimateUpdate] = useState(false)
  const { dialogRef } = Modal.useModal()

  // Vérifier si cette section spécifique a été mise à jour
  const thisSectionWasUpdated = sectionId ? updatedSections[sectionId] : wasUpdated

  useEffect(() => {
    setAnimateUpdate(!!thisSectionWasUpdated)
    const timer = setTimeout(() => {
      setAnimateUpdate(false)
    }, 2000) // Durée de l'animation

    return () => clearTimeout(timer)
  }, [thisSectionWasUpdated])

  const handleCloseModal = () => {
    if(onCloseModalCallback) {
      onCloseModalCallback()
    }
    setModalVisible(false)
  }

  const handleEditClick = () => {
    setModalVisible(true)
  }

  const handleSuccess = () => {
    if (onUpdateSuccess && sectionId) {
      onUpdateSuccess(sectionId)
    }
    handleCloseModal()
  }

  // On peut utiliser ces handlers pour les passer aux composants enfants si nécessaire
  const handlers = {
    onClose: handleCloseModal,
    onOpen: handleEditClick,
    onSuccess: handleSuccess
  }

  // Cloner le bouton d'édition et lui injecter le handler d'ouverture du modal
  const enhancedEditButton = editButton && React.cloneElement(editButton, {
    onEditClick: handleEditClick,
    ...editButton.props
  })

  // Cloner le contenu du modal et lui injecter les handlers
  const enhancedModalContent = modalContent && React.cloneElement(modalContent, {
    onClose: handleCloseModal,
    onSuccess: handleSuccess,
    ...modalContent.props
  })

  // Valeurs à partager via le contexte
  const contextValue = {
    modalVisible,
    setModalVisible,
    animateUpdate,
    setAnimateUpdate,
  }

  return (
    <DetailContext.Provider value={contextValue}>
      <SectionPanel>
        <SectionPanel.Title title={title}>
          {editButton && (
            <div className="flex justify-end mb-4">
              {enhancedEditButton}
            </div>
          )}
        </SectionPanel.Title>
        <SectionPanel.Body {...bodyProps} animateUpdate={animateUpdate}>
          {loading && isPendingAction && (
            <Spinner color='primary' />
          )}
          {!loading && children}
        </SectionPanel.Body>
      </SectionPanel>

      <Modal
        open={modalVisible}
        onClose={handleCloseModal}
        ref={dialogRef}
        backdrop={showBackdrop}
        className="max-w-full rounded-none overflow-hidden"
        {...modalProps}
      >
        <Modal.Header showCloseButton={showCloseButton} />
        <Modal.Body className="overflow-y-auto min-h-[400px]">
          {enhancedModalContent}
        </Modal.Body>
      </Modal>
    </DetailContext.Provider>
  )
}

export default DetailTemplate